import * as React from 'react';
import getAdjustedIconClassName from 'owa-mini-icons/lib/utils/getAdjustedIconClassName';
import { FluentSvgIcon } from 'mini-fluent-svg-icons/lib/components/FluentSvgIcon';
import { HubItemProps } from './HubItem.types';

const hubItemStyles = require('./HubItem.scss');
const classNames = require('classnames/bind');

export default class HubItem extends React.Component<HubItemProps> {
    private handleOnClick = (ev?: React.MouseEvent<any> | React.KeyboardEvent<any>) => {
        const { onClick, id } = this.props;
        const data = id || null;
        onClick?.(data);
        ev?.stopPropagation();
    };

    render() {
        const {
            children,
            onClick,
            styles: propStyles,
            ariaHasPopup,
            ariaExpanded,
            ariaPressed,
            isHubItemNotTabbable,
            forwardRef,
            isSelected,
            onKeyDown,
            role = 'radio',
            adIcon,
        } = this.props;
        let { ariaLabel } = this.props;
        if (!ariaLabel) {
            ariaLabel = this.props.primaryText;
        }

        const styles = propStyles ?? {};
        const { className } = styles;
        const hubClassName = classNames(
            hubItemStyles.hubItemContainer,
            { [hubItemStyles.interactableItem]: !!onClick },
            className
        );

        const tabIndexProps = isHubItemNotTabbable
            ? {}
            : {
                  ref: forwardRef,
                  onClick: this.handleOnClick,
                  onKeyPress: e => {
                      if (e.key !== 'Enter') {
                          return;
                      }
                      this.handleOnClick();
                  },
                  role: 'menuitem',
              };
        const ariaHasPopupProps =
            onClick && ariaHasPopup != undefined ? { 'aria-haspopup': !!ariaHasPopup } : {};
        const ariaExpandedProps =
            onClick && ariaExpanded != undefined ? { 'aria-expanded': !!ariaExpanded } : {};
        const ariaPressedProps =
            onClick && ariaPressed != undefined ? { 'aria-pressed': !!ariaPressed } : {};

        // If there is a child element, just show it inside the item. Otherwise, construct the view from props.
        return (
            <div
                tabIndex={0}
                className={hubClassName}
                {...ariaHasPopupProps}
                {...ariaExpandedProps}
                {...ariaPressedProps}
                aria-label={ariaLabel}
                aria-checked={isSelected}
                aria-invalid="false"
                {...tabIndexProps}
                onKeyDown={onKeyDown}
                role={role}>
                {children || (
                    <>
                        {adIcon ? this.renderAdIcon() : this.renderIcon()}
                        {this.renderText()}
                        {this.renderAdditionalInfo()}
                    </>
                )}
            </div>
        );
    }

    private renderIcon(): React.ReactNode {
        const { icon, onRenderIcon, styles: propStyles } = this.props;
        const styles = propStyles || {};
        const { iconContainerStyles, iconStyles } = styles;
        return (
            <div
                tabIndex={-1}
                aria-hidden
                className={classNames(hubItemStyles.iconContainer, iconContainerStyles)}>
                {onRenderIcon
                    ? onRenderIcon()
                    : !!icon && (
                          <i
                              className={classNames(getAdjustedIconClassName(icon), iconStyles)}
                              aria-hidden="true"
                          />
                      )}
            </div>
        );
    }

    private renderAdIcon(): React.ReactNode {
        const { icon, onRenderIcon, styles: propStyles } = this.props;
        const styles = propStyles || {};
        const { iconContainerStyles, iconStyles } = styles;
        const mergedIconContainerStyles = classNames(
            hubItemStyles.iconContainer,
            iconContainerStyles
        );
        const mergedIconStyles = classNames(hubItemStyles.icon, iconStyles);
        return (
            <div
                tabIndex={-1}
                aria-hidden
                className={classNames(hubItemStyles.iconContainer, iconContainerStyles)}>
                {onRenderIcon ? (
                    <div className={mergedIconContainerStyles}>{onRenderIcon()}</div>
                ) : icon ? (
                    <FluentSvgIcon
                        icon={icon}
                        iconContainerStyles={mergedIconContainerStyles}
                        iconStyles={mergedIconStyles}
                    />
                ) : null}
            </div>
        );
    }

    private renderText(): React.ReactNode {
        const {
            primaryText,
            secondaryText,
            onRenderText,
            styles: propStyles,
            onClick,
        } = this.props;
        const styles = propStyles || {};
        const { textContainerStyles, primaryTextStyles, secondaryTextStyles } = styles;
        return (
            <div
                tabIndex={-1}
                aria-hidden
                className={classNames(hubItemStyles.textContainer, textContainerStyles, {
                    [hubItemStyles.selectableText]: !(onClick || onRenderText),
                })}>
                {onRenderText
                    ? onRenderText()
                    : primaryText && (
                          <>
                              <span
                                  aria-hidden="true"
                                  className={classNames(
                                      hubItemStyles.primaryText,
                                      primaryTextStyles
                                  )}>
                                  {primaryText}
                              </span>
                              {secondaryText && (
                                  <span
                                      aria-hidden="true"
                                      className={classNames(
                                          hubItemStyles.secondaryText,
                                          secondaryTextStyles
                                      )}>
                                      {secondaryText}
                                  </span>
                              )}
                          </>
                      )}
            </div>
        );
    }

    private renderAdditionalInfo(): React.ReactNode {
        const {
            additionalInfoText,
            additionalInfoIcon,
            onRenderAdditionalInfo,
            styles: propStyles,
            onClick,
        } = this.props;
        const styles = propStyles || {};
        const {
            additionalInfoContainerStyles,
            additionalInfoStyles,
            additionalInfoTextStyles,
        } = styles;
        return onRenderAdditionalInfo || additionalInfoText || additionalInfoIcon ? (
            <div
                tabIndex={-1}
                aria-hidden
                className={classNames(
                    hubItemStyles.additionalInfoContainer,
                    additionalInfoContainerStyles,
                    {
                        [hubItemStyles.selectableText]: !(onClick || onRenderAdditionalInfo),
                    }
                )}>
                {onRenderAdditionalInfo
                    ? onRenderAdditionalInfo()
                    : (additionalInfoText || additionalInfoIcon) && (
                          <div
                              tabIndex={-1}
                              aria-hidden="true"
                              className={classNames(
                                  hubItemStyles.additionalInfo,
                                  additionalInfoStyles
                              )}>
                              {additionalInfoText && (
                                  <span
                                      aria-hidden="true"
                                      className={classNames(
                                          hubItemStyles.additionalInfoText,
                                          additionalInfoTextStyles
                                      )}>
                                      {additionalInfoText}
                                  </span>
                              )}
                              {additionalInfoIcon && (
                                  <i className={getAdjustedIconClassName(additionalInfoIcon)} />
                              )}
                          </div>
                      )}
            </div>
        ) : null;
    }
}
